import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ==============================================================
// COMPONENTE DE FIRMA (Reutilizable)
// ==============================================================
function FirmaPad({ onSave, onClear, titulo = "Firma del Responsable" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-2 w-full mt-2">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full h-[140px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-2 left-3 text-[9px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          {titulo}
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[9px] uppercase tracking-wider font-black text-slate-500 hover:text-rose-500 transition-colors cursor-pointer bg-white/50 px-3 py-1 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

// ==============================================================
// MODAL DE ALERTAS
// ==============================================================
function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

// ==============================================================
// CATÁLOGOS OFICIALES
// ==============================================================
const MOTIVOS_RECHAZO = [
  "Mercancía no codificada",
  "Mercancía no ordenada",
  "No llegó el producto / Faltante",
  "Avería / Empaque roto",
  "Fecha de vencimiento corta / Vencida",
  "Problemas de temperatura",
  "Calidad sensorial deficiente (Sabor, olor, color)",
  "Otro"
];

// ==============================================================
// COMPONENTE PRINCIPAL: ACCIÓN CORRECTIVA
// ==============================================================
export default function FormatoAccionCorrectiva({ onBack }) {
  
  const [datos, setDatos] = useState({
    fechaEvento: new Date().toISOString().split('T')[0],
    proveedor: "",
    producto: "",
    lote: "",
    fechaVencimiento: "",
    cantidadRechazada: "",
    motivo: "",
    otroMotivo: "",
    accionCorrectiva: "",
    responsable: "",
    fechaAplicacion: new Date().toISOString().split('T')[0],
    pdv: ""
  });

  const [listaSedes, setListaSedes] = useState([]);
  const [firmaResponsable, setFirmaResponsable] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  const handleChange = (e) => setDatos({ ...datos, [e.target.name]: e.target.value });

  // Cargar sedes dinámicas desde la base de datos al montar el componente
  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${API_URL}/api/sedes`);
        const data = await res.json();
        if (data.success && Array.isArray(data.sedes)) {
          setListaSedes(data.sedes);
        }
      } catch (error) {
        console.error("Error al cargar sedes maestras:", error);
      }
    };
    fetchSedes();
  }, []);

  const handleSubmit = async () => {
    if (!datos.producto || !datos.cantidadRechazada || !datos.motivo || !datos.accionCorrectiva || !datos.responsable) {
      return setCustomAlert({ show: true, message: "Por favor, completa los campos obligatorios (*).", type: "warning" });
    }
    
    if (datos.motivo === "Otro" && !datos.otroMotivo.trim()) {
      return setCustomAlert({ show: true, message: "Por favor, especifica el motivo del rechazo.", type: "warning" });
    }

    if (!firmaResponsable) {
      return setCustomAlert({ show: true, message: "La firma del responsable es obligatoria.", type: "warning" });
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const motivoFinal = datos.motivo === "Otro" ? datos.otroMotivo : datos.motivo;

      const payload = {
        tipo_formulario: "ACCION_CORRECTIVA_MP",
        datosGenerales: { 
          pdv: datos.pdv || "General", 
          fecha: datos.fechaEvento, 
          evaluador: datos.responsable 
        },
        respuestas: {
          proveedor: datos.proveedor,
          producto: datos.producto,
          lote: datos.lote,
          fecha_vencimiento: datos.fechaVencimiento,
          cantidad_rechazada: datos.cantidadRechazada,
          motivo_rechazo: motivoFinal,
          accion_correctiva: datos.accionCorrectiva,
          fecha_aplicacion: datos.fechaAplicacion
        },
        observaciones: {},
        puntaje_global: 0,
        evidencias: { 
          firma_responsable: firmaResponsable 
        }
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "Acción correctiva registrada exitosamente.", type: "success" });
        setTimeout(() => onBack && onBack(), 1500);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar.", type: "error" });
      }
    } catch (error) {
      setCustomAlert({ show: true, message: "Error de conexión con el servidor.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA ROJA / CARMESÍ */}
      <div className="bg-gradient-to-r from-rose-600/90 to-red-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">Registro de Acción Correctiva (Rechazos)</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">Anexo Rechazo MP | Versión: 1</span>
        </div>
      </div>

      {/* ÁREA SCROLLABLE */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 relative custom-scrollbar">
        
        {/* SECCIÓN 1: DATOS DEL PRODUCTO Y LOTE RECHAZADO */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <h2 className="text-sm font-black text-rose-900 uppercase tracking-tight mb-5 flex items-center gap-2">
            <span>📦</span> Trazabilidad del Producto Rechazado
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-5">
            
            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Fecha del Rechazo *</label>
              <input type="date" name="fechaEvento" value={datos.fechaEvento} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm transition-all m-0" />
            </div>

            <div className="md:col-span-4">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Proveedor</label>
              <input type="text" name="proveedor" value={datos.proveedor} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-5">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Producto Rechazado *</label>
              <input type="text" name="producto" value={datos.producto} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Lote</label>
              <input type="text" name="lote" value={datos.lote} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Fecha de Venc.</label>
              <input type="date" name="fechaVencimiento" value={datos.fechaVencimiento} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Cant. Rechazada *</label>
              <input type="number" name="cantidadRechazada" value={datos.cantidadRechazada} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0" />
            </div>

            {/* SEDE (DESPLEGABLE DINÁMICO DESDE LA BASE DE DATOS) */}
            <div className="md:col-span-3">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Sede (PDV) *</label>
              <select name="pdv" value={datos.pdv} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="" disabled>-- Seleccione --</option>
                {listaSedes.map(sede => (
                  <option key={sede.id} value={sede.nombre}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-12">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Motivo del Rechazo *</label>
              <select name="motivo" value={datos.motivo} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="" disabled>-- Seleccione el Motivo --</option>
                {MOTIVOS_RECHAZO.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {datos.motivo === "Otro" && (
              <div className="md:col-span-12 animate-fade-in">
                <label className="block text-[10px] font-black text-rose-700 mb-1.5 uppercase truncate h-4">Especifique el Motivo *</label>
                <input type="text" name="otroMotivo" value={datos.otroMotivo} onChange={handleChange} className="w-full h-[40px] bg-rose-50/50 border border-rose-200 focus:border-rose-500 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-inner m-0" />
              </div>
            )}

          </div>
        </div>

        {/* SECCIÓN 2: ACCIÓN CORRECTIVA Y RESPONSABILIDAD */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80 flex flex-col justify-between">
            <div>
              <h2 className="text-sm font-black text-rose-900 uppercase tracking-tight mb-4 flex items-center gap-2">
                <span>🛠️</span> Plan de Acción
              </h2>

              <div className="mb-5">
                <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase">Acción Correctiva a Tomar *</label>
                <textarea 
                  name="accionCorrectiva" 
                  value={datos.accionCorrectiva} 
                  onChange={handleChange} 
                  rows="3" 
                  className="w-full bg-white/80 border border-slate-300 focus:border-rose-400 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 outline-none shadow-sm resize-none uppercase" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Responsable *</label>
                  <input 
                    type="text" name="responsable" value={datos.responsable} onChange={handleChange} 
                    className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm m-0 uppercase" 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Fecha Aplicación</label>
                  <input type="date" name="fechaAplicacion" value={datos.fechaAplicacion} onChange={handleChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-rose-400 rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm transition-all m-0" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
            <h2 className="text-sm font-black text-rose-900 uppercase tracking-tight mb-3 flex items-center gap-2">
              <span>✍️</span> Validación
            </h2>
            <FirmaPad onSave={setFirmaResponsable} onClear={() => setFirmaResponsable(null)} titulo="Firma del Responsable *" />
            
            <button onClick={handleSubmit} className="w-full mt-5 bg-gradient-to-r from-rose-600 to-red-800 hover:from-rose-500 hover:to-red-700 text-white font-black py-3.5 rounded-xl shadow-lg active:scale-95 transition-all text-xs tracking-widest uppercase cursor-pointer border border-rose-400">
              Registrar Acción
            </button>
          </div>
        </div>

      </div>

      {/* ALERTAS */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}